import type { AvatarConfig } from "@shared/types";

export type RoomPhase = "lobby" | "countdown" | "racing" | "results";

export interface MpPlayer {
  id: string;
  displayName: string;
  avatarConfig: AvatarConfig;
  /** Stable slot index assigned by host; drives lateral offset in the race. */
  slotIdx: number;
  ready: boolean;
  finished: boolean;
  finishTimeMs?: number;
  finishScore?: number;
  finishCorrect?: number;
  finishTotal?: number;
  isBot?: boolean;
}

export interface MpRoomState {
  code: string;
  hostId: string;
  lessonId: string;
  /** Shared seed → identical Path + obstacle layout for all players. */
  seed: number;
  phase: RoomPhase;
  players: MpPlayer[];
  /** Server timestamp (ms) when countdown started; race starts countdownStartedAt + 3000. */
  countdownStartedAt?: number;
  /** Server-bumped rev — clients can detect stale snapshots. */
  rev?: number;
  /** Hard cap on simultaneous players. Mirrors protocol.MAX_PLAYERS. */
  maxPlayers?: number;
  /** Host-controlled match pause. When true every client freezes its
   *  Game and shows a "Paused by host" overlay; only the host can clear
   *  it. Server enforces host-only writes. */
  hostPaused?: boolean;
  /** Post-round level vote (present while phase==="results"). The 3 candidate
   *  lessonIds players pick between for the next round. */
  voteOptions?: string[];
  /** playerId → chosen lessonId. */
  votes?: Record<string, string>;
  /** Epoch-ms when the 30s vote timer expires and the winner auto-loads. */
  voteDeadline?: number;
  /** Host-chosen quiz-style preset id (see data/quizStyles.ts). */
  quizStyle?: string;
}

export interface MpPositionTick {
  s: number;
  laneOffset: number;
  state: "run" | "jump" | "hit" | "finished" | "falling";
  /** Forward velocity along the path (m/s). Used for client-side extrapolation. */
  velocityS?: number;
  /** Lateral velocity (m/s). */
  velocityL?: number;
  /** Monotonic per-player jump counter. Increments on every jump press
   *  (ground OR air). Ghosts watch for changes to replay the second
   *  hop in a double-jump — `state` alone stays "jump" through both,
   *  so the visual would otherwise miss the second pop. */
  jumpSeq?: number;
  /** Authoritative vertical offset above the path's heightAt(s). Lets
   *  remote ghosts mirror the local player's jump arc + landing exactly
   *  instead of guessing with a fixed jumpY target. */
  y?: number;
  /** Vertical velocity (m/s, positive = up). Used for soft extrapolation
   *  between ticks so the jump curve stays smooth between 20Hz packets. */
  vy?: number;
  /** True while feet are on a surface. Drives the run animation; ghost
   *  uses it to decide between run-cycle vs jump-tuck pose. */
  grounded?: boolean;
}

export type MpEvent =
  | { type: "state"; state: MpRoomState }
  | { type: "tick"; from: string; tick: MpPositionTick }
  | { type: "leave"; from: string }
  | { type: "error"; message: string };

export interface MpClient {
  /** Open or join a room. Returns the room state once joined. */
  join(opts: {
    code: string;
    asHost: boolean;
    me: { id: string; displayName: string; avatarConfig: AvatarConfig };
    lessonId?: string;
    seed?: number;
    /** Open rooms appear in the public Open Games browser. */
    visibility?: "open" | "private";
  }): Promise<MpRoomState>;

  setReady(ready: boolean): void;
  startRace(): void;
  /** Host only: return the room from results/racing back to the lobby so
   *  players can race again without re-creating the room. */
  backToLobby(): void;
  /** Host only: change the lesson while in the lobby (clears ready states). */
  changeLesson(lessonId: string): void;
  /** Host only: atomically reset the room to lobby AND switch lesson.
   *  Used by the post-results "Next Level" button so a single client tap
   *  fires one server roundtrip instead of two ordered ones. */
  nextLevel(lessonId: string): void;
  /** Host: replay the same level right now, skipping the lobby. */
  restartSameLevel(): void;
  /** Cast a post-round level vote for one of `state.voteOptions`. Anyone can
   *  vote (not host-only); the server tallies and auto-loads the winner. */
  vote(lessonId: string): void;
  /** Host only: set the room's quiz-style preset (how words are tested).
   *  Persists across rounds + level votes. */
  setQuizStyle(quizStyle: string): void;
  /** Host only: pause / resume the whole match. Other players' clients
   *  freeze and show a "Paused by host" overlay until the host clears
   *  it. Server rejects writes from non-hosts. */
  setHostPaused(paused: boolean): void;
  sendTick(tick: MpPositionTick): void;
  reportFinish(opts: {
    timeMs: number;
    score: number;
    correctCount: number;
    totalQuestions: number;
  }): void;

  on(event: "event", cb: (e: MpEvent) => void): () => void;
  leave(): void;
}

export function newRoomCode(): string {
  // 4 chars, unambiguous (no 0/O/I/1).
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let out = "";
  for (let i = 0; i < 4; i++) out += alphabet[Math.floor(Math.random() * alphabet.length)];
  return out;
}
